import { Component, OnInit, inject } from '@angular/core';
import { take } from 'rxjs';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Calibration, CalibrationSlot } from 'src/app/_models/calibration';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { OxygenCalibration } from 'src/app/_models/oxygen-calibration';
import { PhCalibration } from 'src/app/_models/ph-calibration';
import { TabOptions } from 'src/app/_models/tab-options';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { CalibrationService } from 'src/app/_services/calibration.service';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { DropdownFormFieldComponent } from '../../_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { OptionTabToggleComponent } from '../../_components/selection-controls/option-tab-toggle/option-tab-toggle.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { SpargingCalibration } from 'src/app/_models/sparging-calibration';
import { ModalService } from 'src/app/_services/modal.service';
import { FilterModalComponent } from 'src/app/_components/modals/filter-modal/filter-modal.component';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { Module } from 'src/app/_models/module-enum';
import { deviceNavOptions } from 'src/app/_helpers/device-nav-options';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { EDIT_ACCESS_ROLE_LIST, Role } from 'src/app/_models/role';
import { Device } from 'src/app/_models/device';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { DeviceService } from 'src/app/_services/device.service';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { UnderlinedOptionTabToggleComponent } from 'src/app/_components/selection-controls/underlined-option-tab-toggle/underlined-option-tab-toggle.component';

@Component({
  selector: 'app-device-calibrations',
  template: '',
})
export class DeviceCalibrationsComponent implements OnInit {
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected authenticationService = inject(AuthenticationService);
  protected calibrationService = inject(CalibrationService);
  protected modalService = inject(ModalService);
  protected deviceService = inject(DeviceService);
  protected router = inject(Router);

  // Navigation
  navOptions: TabOptions[];
  deviceId: string;

  // Permissions
  laboratory = this.authenticationService.selectedLaboratory;
  currentUser = this.authenticationService.currentUserValue;

  // Table control variables
  currentFilters = [];
  currentSort: {
    sortBy: string;
    sortDirection: string;
  } = {
    sortBy: 'Name',
    sortDirection: 'asc',
  };

  device: Device;

  // Pagination
  pageRequested = 1;
  pageSize = 5;
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  hasNext = false;
  hasPrevious = false;

  loading = false;
  isProcessing = false;
  error: ErrorObject;

  saveLocationsLoading = false;
  slotList: { key: string; value: string }[] = [];
  calibrationModuleType: Module;

  Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;
  DeviceSetupStatus = DeviceSetupStatus;

  selectedCalibaration:
    | Calibration
    | OxygenCalibration
    | PhCalibration
    | SpargingCalibration;

  calibrationsPerPageField = new FormBase({
    key: 'requestsPerPage',
    label: 'Page Size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5', // Set default value to 5
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  saveLocationField = new FormBase({
    key: 'saveLocation',
    label: 'Select Save Location',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: true,
    value: null,
    options: [],
  });

  tableControls = this.formBuilder.group({
    [this.calibrationsPerPageField.key]: new FormControl(
      this.calibrationsPerPageField.value,
    ),
  });

  calibrationForm = this.formBuilder.group({
    [this.saveLocationField.key]: [
      {
        value: null,
        disabled: true,
      },
      [Validators.minLength(1), Validators.maxLength(100), Validators.required],
    ],
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(take(1)).subscribe((params) => {
      this.deviceId = params.get('id');
      this.navOptions = deviceNavOptions(this.deviceId, this.currentUser?.role);
    });

    if (this.calibrationService.paginationCache !== null) {
      this.pageSize = this.calibrationService.paginationCache.pageSize;
      this.pageRequested = this.calibrationService.paginationCache.pageNumber;
      this.tableControls.patchValue({
        [this.calibrationsPerPageField.key]: this.pageSize.toString(),
      });
    }
  }

  getClasses(sortBy: string) {
    // Get classes for sorting
    const active = this.currentSort.sortBy === sortBy ? 'active' : '';
    return [active];
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get f(): FormGroup['controls'] {
    return this.calibrationForm.controls;
  }

  getDeviceFromApi(): void {
    this.error = null;
    this.deviceService.getDeviceById(this.deviceId).subscribe({
      next: (device: Device) => {
        this.device = device;
      },
      error: (error: ErrorObject) => {
        this.error = error;
      },
    });
  }

  viewCalibration(
    calibrationId: string,
    calibrationType: 'optical-density' | 'oxygen' | 'ph' | 'sparging',
  ) {
    // View calibration
    void this.router.navigate([
      `/calibration/${calibrationType}/${calibrationId}/calibration-data`,
    ]);
  }

  getSaveLocations(deviceId: number, module: Module): void {
    this.saveLocationsLoading = true;
    // Disable dropdowns and clear previous values before fetching save slot locations again
    this.f.saveLocation.disable();
    this.slotList = [];
    this.saveLocationField.options = [];
    this.error = null;
    this.calibrationService.getSlotSaveLocations(deviceId, module).subscribe({
      next: (response: CalibrationSlot[]) => {
        if (response.length > 0) {
          response.forEach((slot: CalibrationSlot) => {
            this.slotList.push({
              key: slot.slotName,
              value: slot.displayName,
            });
          });
          this.saveLocationField.options = this.slotList;
          this.f.saveLocation.enable();
          this.saveLocationsLoading = false;
          this.openModal('resend-calibration');
        } else {
          // If there are no calibrations set list to null not empty array
          // so the loading spinner/no calibrations message renders correctly
          this.slotList = null;
          this.error = {
            status: -1,
            message: 'No save locations available.',
            code: null,
          };
          this.openModal('error');
        }
        this.saveLocationsLoading = false;
      },
      error: (error: ErrorObject) => {
        this.saveLocationsLoading = false;
        this.error = error;
        this.openModal('error');
      },
    });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }
}

@Component({
  selector: 'app-od-calibrations',
  templateUrl: './od-calibrations/od-calibrations.component.html',
  styleUrls: ['./device-calibrations.component.scss'],
  imports: [
    UnderlinedOptionTabToggleComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    IconButtonComponent,
    DatePipe,
    LoadingSpinnerComponent,
    FilterModalComponent,
    ModalComponent,
    RoleCheckPipe,
    MatOutlinedButtonComponent,
    StatusIndicatorComponent,
  ],
})
export class OdCalibrationsComponent
  extends DeviceCalibrationsComponent
  implements OnInit
{
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected authenticationService = inject(AuthenticationService);
  protected calibrationService = inject(CalibrationService);
  protected router = inject(Router);
  // Calibration vars
  calibrations: Calibration[];

  ngOnInit(): void {
    super.ngOnInit();
    this.getDeviceFromApi();
    this.getODCalibrations();

    // Subscribe to changes in the page size dropdown
    this.tableControls
      .get(this.calibrationsPerPageField.key)
      .valueChanges.subscribe((numOfCalibrations) => {
        this.pageSize = numOfCalibrations as number;
        // Reset page number to 1
        this.pageRequested = 1;
        this.getODCalibrations();
      });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'resend-submit-button':
        this.resendCalibration();
        break;
      case 'resend-close-button':
        this.closeModal('resend-calibration');
        break;
      case 'error-close-button':
        this.closeModal('error');
        break;
      default:
        break;
    }
  }

  handleModal(calibration: Calibration) {
    this.selectedCalibaration = calibration;
    this.getSaveLocations(this.device.id, Module.BaseModule);
  }

  resendCalibration(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.error = null;
    this.calibrationService
      .resendCalibrationToDevice(
        this.selectedCalibaration.id,
        this.f.saveLocation.value,
      )
      .subscribe({
        next: () => {
          this.closeModal('resend-calibration');
          this.getODCalibrations();
          this.calibrationForm.reset();
          this.isProcessing = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isProcessing = false;
          this.closeModal('resend-calibration');
          this.openModal('error');
        },
      });
  }

  getPrevious() {
    // Get previous page
    this.pageRequested = this.currentPage - 1;
    this.getODCalibrations();
  }

  getNext() {
    // Get next page
    this.pageRequested = this.currentPage + 1;
    this.getODCalibrations();
  }

  onSort(sortBy: string) {
    // Sort table by column
    if (this.currentSort.sortBy === sortBy) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortBy;
      this.currentSort.sortDirection = 'asc';
    }
    this.getODCalibrations();
  }

  getODCalibrations() {
    if (this.loading) return;
    this.loading = true;
    this.error = null;
    // Get OD calibrations
    this.calibrationService
      .getCalibrationsByDeviceId(
        parseInt(this.deviceId),
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageSize,
        this.pageRequested,
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.calibrations = response.calibrationList;
          this.totalCount = response.paginationData.TotalCount;
          this.totalPages = response.paginationData.TotalPages;
          this.currentPage = response.paginationData.CurrentPage;
          this.hasNext = response.paginationData.HasNext;
          this.hasPrevious = response.paginationData.HasPrevious;
        },
        error: (error: ErrorObject) => {
          this.loading = false;
          this.error = error;
        },
      });
  }
}

@Component({
  selector: 'app-oxygen-calibrations',
  templateUrl: './oxygen-calibrations/oxygen-calibrations.component.html',
  styleUrls: ['./device-calibrations.component.scss'],
  imports: [
    UnderlinedOptionTabToggleComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    IconButtonComponent,
    DatePipe,
    LoadingSpinnerComponent,
    FilterModalComponent,
    ModalComponent,
    RoleCheckPipe,
    MatOutlinedButtonComponent,
    StatusIndicatorComponent,
  ],
})
export class OxygenCalibrationsComponent
  extends DeviceCalibrationsComponent
  implements OnInit
{
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected authenticationService = inject(AuthenticationService);
  protected calibrationService = inject(CalibrationService);
  protected router = inject(Router);
  // Calibration vars
  oxygenCalibrations: OxygenCalibration[];

  ngOnInit(): void {
    super.ngOnInit();
    this.getDeviceFromApi();
    this.getOxygenCalibrations();

    // Subscribe to changes in the page size dropdown
    this.tableControls
      .get(this.calibrationsPerPageField.key)
      .valueChanges.subscribe((numOfCalibrations) => {
        this.pageSize = numOfCalibrations as number;
        // Reset page number to 1
        this.pageRequested = 1;
        this.getOxygenCalibrations();
      });
  }

  getPrevious() {
    // Get previous page
    this.pageRequested = this.currentPage - 1;
    this.getOxygenCalibrations();
  }

  getNext() {
    // Get next page
    this.pageRequested = this.currentPage + 1;
    this.getOxygenCalibrations();
  }

  onSort(sortBy: string) {
    // Sort table by column
    // Sort table by column
    if (this.currentSort.sortBy === sortBy) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortBy;
      this.currentSort.sortDirection = 'asc';
    }
    this.getOxygenCalibrations();
  }

  getOxygenCalibrations() {
    if (this.loading) return;
    // Get Oxygen calibrations
    this.loading = true;
    this.error = null;
    // Get Oxygen calibrations
    this.calibrationService
      .getOxygenCalibrationsByDeviceId(
        this.deviceId,
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageSize,
        this.pageRequested,
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.oxygenCalibrations = response.calibrationList;
          this.totalCount = response.paginationData.TotalCount;
          this.totalPages = response.paginationData.TotalPages;
          this.currentPage = response.paginationData.CurrentPage;
          this.hasNext = response.paginationData.HasNext;
          this.hasPrevious = response.paginationData.HasPrevious;
        },
        error: (error: ErrorObject) => {
          this.loading = false;
          this.error = error;
        },
      });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'resend-submit-button':
        this.resendOxygenCalibration();
        break;
      case 'resend-close-button':
        this.closeModal('resend-calibration');
        break;
      case 'error-close-button':
        this.closeModal('error');
        break;
      default:
        break;
    }
  }

  handleModal(calibration: OxygenCalibration) {
    this.selectedCalibaration = calibration;
    this.getSaveLocations(this.device.id, Module.Oxygen);
  }

  resendOxygenCalibration(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.error = null;
    this.calibrationService
      .resendOxygenCalibrationToDevice(
        this.selectedCalibaration.id,
        this.f.saveLocation.value,
      )
      .subscribe({
        next: () => {
          this.closeModal('resend-calibration');
          this.getOxygenCalibrations();
          this.calibrationForm.reset();
          this.isProcessing = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isProcessing = false;
          this.closeModal('resend-calibration');
          this.openModal('error');
        },
      });
  }
}

@Component({
  selector: 'app-ph-calibrations',
  templateUrl: './ph-calibrations/ph-calibrations.component.html',
  styleUrls: ['./device-calibrations.component.scss'],
  imports: [
    UnderlinedOptionTabToggleComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    IconButtonComponent,
    DatePipe,
    LoadingSpinnerComponent,
    FilterModalComponent,
    ModalComponent,
    RoleCheckPipe,
    MatOutlinedButtonComponent,
    StatusIndicatorComponent,
  ],
})
export class PhCalibrationsComponent
  extends DeviceCalibrationsComponent
  implements OnInit
{
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected authenticationService = inject(AuthenticationService);
  protected calibrationService = inject(CalibrationService);
  protected router = inject(Router);
  // Calibration vars
  phCalibrations: PhCalibration[];

  ngOnInit(): void {
    super.ngOnInit();
    this.getDeviceFromApi();
    this.getPhCalibrations();

    // Subscribe to changes in the page size dropdown
    this.tableControls
      .get(this.calibrationsPerPageField.key)
      .valueChanges.subscribe((numOfCalibrations) => {
        this.pageSize = numOfCalibrations as number;
        // Reset page number to 1
        this.pageRequested = 1;
        this.getPhCalibrations();
      });
  }

  getPrevious() {
    // Get previous page
    this.pageRequested = this.currentPage - 1;
    this.getPhCalibrations();
  }

  getNext() {
    // Get next page
    this.pageRequested = this.currentPage + 1;
    this.getPhCalibrations();
  }

  onSort(sortBy: string) {
    // Sort table by column
    if (this.currentSort.sortBy === sortBy) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortBy;
      this.currentSort.sortDirection = 'asc';
    }
    this.getPhCalibrations();
  }

  getPhCalibrations() {
    if (this.loading) return;
    // Get pH calibrations
    this.loading = true;
    this.error = null;
    // Get pH calibrations
    this.calibrationService
      .getPHCalibrationsByDeviceId(
        this.deviceId,
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageSize,
        this.pageRequested,
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.phCalibrations = response.calibrationList;
          this.totalCount = response.paginationData.TotalCount;
          this.totalPages = response.paginationData.TotalPages;
          this.currentPage = response.paginationData.CurrentPage;
          this.hasNext = response.paginationData.HasNext;
          this.hasPrevious = response.paginationData.HasPrevious;
        },
        error: (error: ErrorObject) => {
          this.loading = false;
          this.error = error;
        },
      });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'resend-submit-button':
        this.resendPHCalibration();
        break;
      case 'resend-close-button':
        this.closeModal('resend-calibration');
        break;
      case 'error-close-button':
        this.closeModal('error');
        break;
      default:
        break;
    }
  }

  handleModal(calibration: PhCalibration) {
    this.selectedCalibaration = calibration;
    this.getSaveLocations(this.device.id, Module.pH);
  }

  resendPHCalibration(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.error = null;
    this.calibrationService
      .resendPHCalibrationToDevice(
        this.selectedCalibaration.id,
        this.f.saveLocation.value,
      )
      .subscribe({
        next: () => {
          this.closeModal('resend-calibration');
          this.getPhCalibrations();
          this.calibrationForm.reset();
          this.isProcessing = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isProcessing = false;
          this.closeModal('resend-calibration');
          this.openModal('error');
        },
      });
  }
}

@Component({
  selector: 'app-sparging-calibrations',
  templateUrl: './sparging-calibrations/sparging-calibrations.component.html',
  styleUrls: ['./device-calibrations.component.scss'],
  imports: [
    UnderlinedOptionTabToggleComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    IconButtonComponent,
    DatePipe,
    LoadingSpinnerComponent,
    FilterModalComponent,
    ModalComponent,
    RoleCheckPipe,
    MatOutlinedButtonComponent,
    StatusIndicatorComponent,
  ],
})
export class SpargingCalibrationsComponent
  extends DeviceCalibrationsComponent
  implements OnInit
{
  protected route = inject(ActivatedRoute);
  protected formBuilder = inject(FormBuilder);
  protected authenticationService = inject(AuthenticationService);
  protected calibrationService = inject(CalibrationService);
  protected router = inject(Router);
  // Calibration vars
  spargingCalibrations: SpargingCalibration[];

  ngOnInit(): void {
    super.ngOnInit();
    this.getDeviceFromApi();
    this.getSpargingCalibrations();

    // Subscribe to changes in the page size dropdown
    this.tableControls
      .get(this.calibrationsPerPageField.key)
      .valueChanges.subscribe((numOfCalibrations) => {
        this.pageSize = numOfCalibrations as number;
        // Reset page number to 1
        this.pageRequested = 1;
        this.getSpargingCalibrations();
      });
  }

  getPrevious() {
    // Get previous page
    this.pageRequested = this.currentPage - 1;
    this.getSpargingCalibrations();
  }

  getNext() {
    // Get next page
    this.pageRequested = this.currentPage + 1;
    this.getSpargingCalibrations();
  }

  onSort(sortBy: string) {
    // Sort table by column
    if (this.currentSort.sortBy === sortBy) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortBy;
      this.currentSort.sortDirection = 'asc';
    }
    this.getSpargingCalibrations();
  }

  getSpargingCalibrations() {
    if (this.loading) return;
    // Get sparging calibrations
    this.loading = true;
    this.error = null;
    // Get sparging calibrations
    this.calibrationService
      .getSpargingCalibrationsByDeviceId(
        this.deviceId,
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageSize,
        this.pageRequested,
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.spargingCalibrations = response.calibrationList;
          this.totalCount = response.paginationData.TotalCount;
          this.totalPages = response.paginationData.TotalPages;
          this.currentPage = response.paginationData.CurrentPage;
          this.hasNext = response.paginationData.HasNext;
          this.hasPrevious = response.paginationData.HasPrevious;
        },
        error: (error: ErrorObject) => {
          this.loading = false;
          this.error = error;
        },
      });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'resend-submit-button':
        this.resendSpargingCalibration();
        break;
      case 'resend-close-button':
        this.closeModal('resend-calibration');
        break;
      case 'error-close-button':
        this.closeModal('error');
        break;
      default:
        break;
    }
  }

  handleModal(calibration: SpargingCalibration) {
    this.selectedCalibaration = calibration;
    this.getSaveLocations(this.device.id, Module.Sparging);
  }

  resendSpargingCalibration(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.error = null;
    this.calibrationService
      .resendSpargingCalibrationToDevice(
        this.selectedCalibaration.id,
        this.f.saveLocation.value,
      )
      .subscribe({
        next: () => {
          this.closeModal('resend-calibration');
          this.getSpargingCalibrations();
          this.calibrationForm.reset();
          this.isProcessing = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isProcessing = false;
          this.closeModal('resend-calibration');
          this.openModal('error');
        },
      });
  }
}
